import React, { useState, useEffect } from 'react';
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { 
  X, Shield, Search, Check, AlertTriangle, CreditCard, 
  Loader2, Tag, Mail, Globe, Clock, ChevronRight, Package, Sparkles
} from 'lucide-react';

const SERVICES = {
  vulnerability_assessment: {
    id: "vulnerability_assessment",
    name: "Vulnerability Assessment Esteso",
    description: "Identificazione vulnerabilità del server e piano di remediation",
    features: [
      "Scansione completa delle vulnerabilità",
      "Test di penetrazione automatizzato",
      "Report dettagliato delle vulnerabilità",
      "Piano di remediation"
    ],
    price_net: 250,
    price_gross: 305,
    icon: Shield,
    color: "from-red-500 to-orange-500"
  },
  cyber_investigation: {
    id: "cyber_investigation",
    name: "Smart Cyber Investigation",
    description: "Dark Web check, Cyber Risk Score, impatto economico, remediation",
    features: [
      "Ricerca dati nel Dark Web",
      "Calcolo Cyber Risk Score",
      "Stima impatto economico attacco",
      "Remediation implementabili"
    ],
    price_net: 100,
    price_gross: 122,
    icon: Search,
    color: "from-cyan-500 to-blue-500"
  },
  bundle_completo: {
    id: "bundle_completo",
    name: "Bundle Completo",
    description: "Entrambi i servizi insieme con sconto speciale del 5%",
    features: [
      "Vulnerability Assessment Esteso",
      "Smart Cyber Investigation",
      "Risparmio di €21 rispetto all'acquisto separato",
      "Report unificato completo"
    ],
    price_net: 333,
    price_gross: 406,
    icon: Package,
    color: "from-purple-500 to-pink-500",
    is_bundle: true,
    savings: 21
  }
};

// Helper per formattare il prezzo senza decimali
const formatPrice = (price) => `€${Math.round(price)}`;

const PremiumServicesModal = ({ isOpen, onClose, analyzedDomain, preSelectedService }) => {
  const [step, setStep] = useState('select'); // select, details, payment, success
  const [selectedService, setSelectedService] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [targetUrl, setTargetUrl] = useState(analyzedDomain || '');
  const [customerEmail, setCustomerEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState('');
  
  // Accettazione policy
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptManleva, setAcceptManleva] = useState(false);
  const [acceptTermsManleva, setAcceptTermsManleva] = useState(false); // Combined checkbox
  
  // Link alle policy (caricati dal backend)
  const [policyLinks, setPolicyLinks] = useState({
    privacy_policy_url: '#',
    terms_conditions_url: '#',
    manleva_url: '#',
    combine_terms_manleva: false
  });
  
  // Dati fatturazione
  const [billingData, setBillingData] = useState({
    companyName: '',
    partitaIva: '',
    codiceFiscale: '',
    pecSdi: '',
    address: '',
    city: '',
    cap: '',
    province: ''
  });
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  // Check if all policies are accepted (handling combined case)
  const allPoliciesAccepted = policyLinks.combine_terms_manleva 
    ? (acceptPrivacy && acceptTermsManleva)  // Combined: Privacy + Terms&Manleva
    : (acceptPrivacy && acceptTerms && acceptManleva);  // Separate: Privacy + Terms + Manleva

  // Load policy links from backend
  useEffect(() => {
    const loadPolicyLinks = async () => {
      try {
        const res = await fetch(`${backendUrl}/api/policy-links`);
        if (res.ok) {
          const data = await res.json();
          setPolicyLinks(data);
        }
      } catch (err) {
        console.error('Error loading policy links:', err);
      }
    };
    loadPolicyLinks();
  }, [backendUrl]);

  useEffect(() => {
    if (analyzedDomain) {
      setTargetUrl(analyzedDomain);
    }
  }, [analyzedDomain]);

  // Handle pre-selected service from URL
  useEffect(() => {
    if (preSelectedService && SERVICES[preSelectedService] && isOpen) {
      setSelectedService(preSelectedService);
      setStep('details');
    }
  }, [preSelectedService, isOpen]);

  const getFinalPrice = () => {
    if (!selectedService) return 0;
    const service = SERVICES[selectedService];
    if (couponApplied) {
      return Math.round(couponApplied.final_price);
    }
    return service.price_gross;
  };

  const getDiscountAmount = () => {
    if (!couponApplied) return 0;
    return Math.round(couponApplied.discount_amount);
  };

  const validateCoupon = async () => {
    if (!couponCode.trim() || !selectedService) return;
    
    setCouponLoading(true);
    setCouponError('');
    setCouponApplied(null);
    
    try {
      const response = await fetch(`${backendUrl}/api/validate-coupon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: couponCode,
          service_id: selectedService
        })
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Coupon non valido');
      }
      
      const data = await response.json();
      setCouponApplied(data);
    } catch (err) {
      setCouponError(err.message);
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setCouponApplied(null);
    setCouponCode('');
    setCouponError('');
  };

  const handlePayment = async () => {
    if (!targetUrl || !customerEmail) {
      setError('Compila tutti i campi obbligatori');
      return;
    }
    
    // Validazione dati fatturazione
    if (!billingData.partitaIva && !billingData.codiceFiscale) {
      setError('Inserisci Partita IVA o Codice Fiscale');
      return;
    }
    
    if (!billingData.pecSdi) {
      setError('Inserisci PEC o Codice SDI per la fatturazione elettronica');
      return;
    }
    
    setLoading(true);
    setError('');
    setRedirecting(false);
    
    try {
      console.log('Creating order...', { service_id: selectedService, payment_method: paymentMethod });
      
      const response = await fetch(`${backendUrl}/api/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          service_id: selectedService,
          target_url: targetUrl,
          customer_email: customerEmail,
          payment_method: paymentMethod,
          coupon_code: couponApplied?.code || null,
          origin_url: window.location.origin,
          billing_data: billingData
        })
      });
      
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Errore nella creazione dell\'ordine');
      }
      
      const data = await response.json();
      console.log('Order response:', data);
      
      if (paymentMethod === 'stripe' && data.checkout_url) {
        console.log('Opening Stripe checkout:', data.checkout_url);
        // Show redirecting state
        setRedirecting(true);
        setLoading(false);
        
        // Open in new tab for better browser compatibility
        const stripeWindow = window.open(data.checkout_url, '_blank');
        
        if (!stripeWindow || stripeWindow.closed || typeof stripeWindow.closed === 'undefined') {
          // Popup blocked - fallback to same window redirect
          console.log('Popup blocked, redirecting in same window');
          window.location.href = data.checkout_url;
        } else {
          // Show message to user
          setRedirecting(false);
          setError('');
          alert('La pagina di pagamento Stripe si è aperta in una nuova scheda. Completa il pagamento e torna qui.');
        }
        return;
      } else if (paymentMethod === 'paypal') {
        setStep('payment');
        setLoading(false);
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'Errore durante il pagamento');
      setLoading(false);
      setRedirecting(false);
    }
  };

  const handlePayPalApprove = async (data, actions) => {
    try {
      const response = await fetch(`${backendUrl}/api/paypal-capture?transaction_id=${data.orderID}&paypal_order_id=${data.orderID}`, {
        method: 'POST'
      });
      
      if (response.ok) {
        setStep('success');
      }
    } catch (err) {
      setError('Errore nel completamento del pagamento');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-[#1a1a2e] rounded-2xl border border-gray-700 shadow-2xl">
        {/* Close button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white transition-colors z-10"
        >
          <X className="h-6 w-6" />
        </button>

        {/* Header */}
        <div className="p-6 pb-0">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-gradient-to-r from-red-500 to-orange-500 rounded-lg">
              <Shield className="h-6 w-6 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white">
              {step === 'success' ? '✅ Ordine Completato!' : 'Servizi Premium'}
            </h2>
          </div>
          {step !== 'success' && (
            <p className="text-gray-400 text-sm">
              Approfondisci l'analisi di sicurezza con i nostri servizi avanzati
            </p>
          )}
        </div>

        {/* Content based on step */}
        <div className="p-6">
          {step === 'select' && (
            <>
              {/* Service Cards */}
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                {Object.entries(SERVICES).map(([key, service]) => {
                  const Icon = service.icon;
                  const isSelected = selectedService === key;
                  const isBundle = service.is_bundle;
                  
                  return (
                    <div
                      key={key}
                      onClick={() => setSelectedService(key)}
                      className={`relative p-5 rounded-xl border-2 cursor-pointer transition-all ${
                        isSelected 
                          ? 'border-red-500 bg-red-500/10' 
                          : 'border-gray-700 hover:border-gray-600 bg-[#0f0f1a]'
                      } ${isBundle ? 'md:col-span-3 md:flex md:items-center md:gap-6' : ''}`}
                    >
                      {isSelected && (
                        <div className="absolute top-3 right-3">
                          <Check className="h-5 w-5 text-red-500" />
                        </div>
                      )}
                      
                      {isBundle && (
                        <div className="absolute -top-3 left-4">
                          <span className="px-3 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold rounded-full flex items-center gap-1">
                            <Sparkles className="h-3 w-3" /> RISPARMIA {formatPrice(service.savings)}
                          </span>
                        </div>
                      )}
                      
                      <div className={isBundle ? 'md:flex-shrink-0' : ''}>
                        <div className={`inline-flex p-2 rounded-lg bg-gradient-to-r ${service.color} mb-3`}>
                          <Icon className="h-5 w-5 text-white" />
                        </div>
                        
                        <h3 className="text-lg font-semibold text-white mb-1">{service.name}</h3>
                        <p className="text-gray-400 text-sm mb-3">{service.description}</p>
                      </div>
                      
                      <div className={isBundle ? 'md:flex-1' : ''}>
                        <ul className={`space-y-1 mb-4 ${isBundle ? 'md:grid md:grid-cols-2 md:gap-x-4 md:space-y-0' : ''}`}>
                          {service.features.map((feature, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm text-gray-300">
                              <Check className="h-3 w-3 text-green-400 flex-shrink-0" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className={`flex items-center justify-between pt-3 border-t border-gray-700 ${isBundle ? 'md:flex-shrink-0 md:flex-col md:items-end md:border-t-0 md:border-l md:pl-6 md:pt-0' : ''}`}>
                        <div className={isBundle ? 'text-right' : ''}>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400 text-sm">{formatPrice(service.price_net)}</span>
                            <span className="text-gray-500 text-xs">+ IVA 22%</span>
                          </div>
                          <p className="text-xl font-bold text-white">{formatPrice(service.price_gross)}</p>
                          <span className="text-green-400 text-xs font-medium">IVA inclusa</span>
                        </div>
                        {!isBundle && (
                          <div className="flex items-center gap-1 text-gray-400 text-xs">
                            <Clock className="h-3 w-3" />
                            Entro 24h
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <button
                onClick={() => selectedService && setStep('details')}
                disabled={!selectedService}
                className={`w-full py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
                  selectedService
                    ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white hover:opacity-90'
                    : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                }`}
              >
                Continua <ChevronRight className="h-4 w-4" />
              </button>
            </>
          )}

          {step === 'details' && selectedService && (
            <>
              {/* Selected Service Summary */}
              <div className="bg-[#0f0f1a] rounded-xl p-4 mb-6 border border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-white">{SERVICES[selectedService].name}</h4>
                    <p className="text-gray-400 text-sm">Consegna entro 24 ore</p>
                  </div>
                  <div className="text-right">
                    {couponApplied && (
                      <span className="text-gray-400 line-through text-sm">
                        {formatPrice(SERVICES[selectedService].price_gross)}
                      </span>
                    )}
                    <p className="text-xl font-bold text-white">{formatPrice(getFinalPrice())}</p>
                    <span className="text-green-400 text-xs">IVA inclusa</span>
                  </div>
                </div>
              </div>

              {/* Form Fields */}
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <Globe className="inline h-4 w-4 mr-1" />
                    URL da Analizzare *
                  </label>
                  <input
                    type="text"
                    value={targetUrl}
                    onChange={(e) => setTargetUrl(e.target.value)}
                    placeholder="esempio.com"
                    className="w-full px-4 py-3 bg-[#0f0f1a] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-red-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <Mail className="inline h-4 w-4 mr-1" />
                    Email dove inviare il report *
                  </label>
                  <input
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    placeholder="tuaemail@esempio.com"
                    className="w-full px-4 py-3 bg-[#0f0f1a] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-red-500 focus:outline-none"
                  />
                </div>

                {/* Billing Data Section */}
                <div className="border-t border-gray-700 pt-4 mt-4">
                  <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                    📄 Dati di Fatturazione
                  </h4>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-400 mb-1">
                        Ragione Sociale / Nome e Cognome
                      </label>
                      <input
                        type="text"
                        value={billingData.companyName}
                        onChange={(e) => setBillingData({...billingData, companyName: e.target.value})}
                        placeholder="Es. Mario Rossi SRL"
                        className="w-full px-3 py-2 bg-[#0f0f1a] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-red-500 focus:outline-none text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1">
                        Partita IVA *
                      </label>
                      <input
                        type="text"
                        value={billingData.partitaIva}
                        onChange={(e) => setBillingData({...billingData, partitaIva: e.target.value.toUpperCase()})}
                        placeholder="IT12345678901"
                        maxLength={13}
                        className="w-full px-3 py-2 bg-[#0f0f1a] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-red-500 focus:outline-none text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1">
                        Codice Fiscale *
                      </label>
                      <input
                        type="text"
                        value={billingData.codiceFiscale}
                        onChange={(e) => setBillingData({...billingData, codiceFiscale: e.target.value.toUpperCase()})}
                        placeholder="RSSMRA80A01H501U"
                        maxLength={16}
                        className="w-full px-3 py-2 bg-[#0f0f1a] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-red-500 focus:outline-none text-sm"
                      />
                    </div>
                    
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-400 mb-1">
                        PEC o Codice SDI * (per fatturazione elettronica)
                      </label>
                      <input
                        type="text"
                        value={billingData.pecSdi}
                        onChange={(e) => setBillingData({...billingData, pecSdi: e.target.value})}
                        placeholder="pec@esempio.it oppure 0000000"
                        className="w-full px-3 py-2 bg-[#0f0f1a] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-red-500 focus:outline-none text-sm"
                      />
                    </div>
                    
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-400 mb-1">
                        Indirizzo
                      </label>
                      <input
                        type="text"
                        value={billingData.address}
                        onChange={(e) => setBillingData({...billingData, address: e.target.value})}
                        placeholder="Via Roma, 1"
                        className="w-full px-3 py-2 bg-[#0f0f1a] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-red-500 focus:outline-none text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1">
                        Città
                      </label>
                      <input
                        type="text"
                        value={billingData.city}
                        onChange={(e) => setBillingData({...billingData, city: e.target.value})}
                        placeholder="Milano"
                        className="w-full px-3 py-2 bg-[#0f0f1a] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-red-500 focus:outline-none text-sm"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">
                          CAP
                        </label>
                        <input
                          type="text"
                          value={billingData.cap}
                          onChange={(e) => setBillingData({...billingData, cap: e.target.value})}
                          placeholder="20100"
                          maxLength={5}
                          className="w-full px-3 py-2 bg-[#0f0f1a] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-red-500 focus:outline-none text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">
                          Prov.
                        </label>
                        <input
                          type="text"
                          value={billingData.province}
                          onChange={(e) => setBillingData({...billingData, province: e.target.value.toUpperCase()})}
                          placeholder="MI"
                          maxLength={2}
                          className="w-full px-3 py-2 bg-[#0f0f1a] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-red-500 focus:outline-none text-sm"
                        />
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-500 text-xs mt-2">* Inserisci Partita IVA o Codice Fiscale (almeno uno obbligatorio)</p>
                </div>

                {/* Coupon Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <Tag className="inline h-4 w-4 mr-1" />
                    Codice Sconto
                  </label>
                  {couponApplied ? (
                    <div className="flex items-center justify-between bg-green-500/10 border border-green-500/30 rounded-lg px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-green-400" />
                        <span className="text-green-400 font-medium">{couponApplied.code}</span>
                        <span className="text-gray-400">(-{couponApplied.discount_percent}%)</span>
                      </div>
                      <button 
                        onClick={removeCoupon}
                        className="text-gray-400 hover:text-white"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        placeholder="Inserisci codice"
                        className="flex-1 px-4 py-3 bg-[#0f0f1a] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-red-500 focus:outline-none"
                      />
                      <button
                        onClick={validateCoupon}
                        disabled={couponLoading || !couponCode.trim()}
                        className="px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg disabled:opacity-50 transition-colors"
                      >
                        {couponLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Applica'}
                      </button>
                    </div>
                  )}
                  {couponError && (
                    <p className="text-red-400 text-sm mt-2">{couponError}</p>
                  )}
                </div>

                {/* Payment Method Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <CreditCard className="inline h-4 w-4 mr-1" />
                    Metodo di Pagamento
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setPaymentMethod('stripe')}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        paymentMethod === 'stripe'
                          ? 'border-red-500 bg-red-500/10'
                          : 'border-gray-700 hover:border-gray-600'
                      }`}
                    >
                      <div className="text-center">
                        <div className="text-2xl mb-1">💳</div>
                        <span className="text-white font-medium">Carta di Credito</span>
                        <p className="text-gray-400 text-xs">Visa, Mastercard, Amex</p>
                      </div>
                    </button>
                    <button
                      onClick={() => setPaymentMethod('paypal')}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        paymentMethod === 'paypal'
                          ? 'border-red-500 bg-red-500/10'
                          : 'border-gray-700 hover:border-gray-600'
                      }`}
                    >
                      <div className="text-center">
                        <div className="text-2xl mb-1">🅿️</div>
                        <span className="text-white font-medium">PayPal</span>
                        <p className="text-gray-400 text-xs">Conto PayPal</p>
                      </div>
                    </button>
                  </div>
                </div>
              </div>

              {/* Price Summary */}
              <div className="bg-[#0f0f1a] rounded-xl p-4 mb-6 border border-gray-700">
                <div className="space-y-2">
                  <div className="flex justify-between text-gray-400">
                    <span>Imponibile</span>
                    <span>{formatPrice(SERVICES[selectedService].price_net)}</span>
                  </div>
                  <div className="flex justify-between text-gray-400">
                    <span>IVA (22%)</span>
                    <span>{formatPrice(SERVICES[selectedService].price_gross - SERVICES[selectedService].price_net)}</span>
                  </div>
                  {couponApplied && (
                    <div className="flex justify-between text-green-400">
                      <span>Sconto ({couponApplied.discount_percent}%)</span>
                      <span>-{formatPrice(getDiscountAmount())}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-white font-bold pt-2 border-t border-gray-700">
                    <span>Totale (IVA inclusa)</span>
                    <span>{formatPrice(getFinalPrice())}</span>
                  </div>
                </div>
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-400" />
                  <span className="text-red-400">{error}</span>
                </div>
              )}

              {/* Policy Acceptance Checkboxes */}
              <div className="bg-gray-800/50 rounded-lg p-4 mb-4 space-y-3">
                <p className="text-sm text-gray-400 mb-3">Per procedere con l'ordine, è necessario accettare:</p>
                
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={acceptPrivacy}
                    onChange={(e) => setAcceptPrivacy(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-600 bg-gray-700 text-red-500 focus:ring-red-500 focus:ring-offset-gray-900"
                  />
                  <span className="text-sm text-gray-300 group-hover:text-white transition-colors">
                    Ho letto e accetto la{' '}
                    <a 
                      href={policyLinks.privacy_policy_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-cyan-400 hover:text-cyan-300 underline"
                    >
                      Privacy Policy
                    </a>
                  </span>
                </label>
                
                {/* Conditional rendering based on combine_terms_manleva setting */}
                {policyLinks.combine_terms_manleva ? (
                  /* Combined Terms & Manleva checkbox */
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={acceptTermsManleva}
                      onChange={(e) => setAcceptTermsManleva(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-gray-600 bg-gray-700 text-red-500 focus:ring-red-500 focus:ring-offset-gray-900"
                    />
                    <span className="text-sm text-gray-300 group-hover:text-white transition-colors">
                      Ho letto e accetto le{' '}
                      <a 
                        href={policyLinks.terms_conditions_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-cyan-400 hover:text-cyan-300 underline"
                      >
                        Condizioni Generali e Manleva
                      </a>
                    </span>
                  </label>
                ) : (
                  /* Separate Terms and Manleva checkboxes */
                  <>
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={acceptTerms}
                        onChange={(e) => setAcceptTerms(e.target.checked)}
                        className="mt-1 h-4 w-4 rounded border-gray-600 bg-gray-700 text-red-500 focus:ring-red-500 focus:ring-offset-gray-900"
                      />
                      <span className="text-sm text-gray-300 group-hover:text-white transition-colors">
                        Ho letto e accetto le{' '}
                        <a 
                          href={policyLinks.terms_conditions_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-cyan-400 hover:text-cyan-300 underline"
                        >
                          Condizioni Generali di Fornitura
                        </a>
                      </span>
                    </label>
                    
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={acceptManleva}
                        onChange={(e) => setAcceptManleva(e.target.checked)}
                        className="mt-1 h-4 w-4 rounded border-gray-600 bg-gray-700 text-red-500 focus:ring-red-500 focus:ring-offset-gray-900"
                      />
                      <span className="text-sm text-gray-300 group-hover:text-white transition-colors">
                        Ho letto e accetto la{' '}
                        <a 
                          href={policyLinks.manleva_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-cyan-400 hover:text-cyan-300 underline"
                        >
                          Manleva
                        </a>
                      </span>
                    </label>
                  </>
                )}
                
                {!allPoliciesAccepted && (
                  <p className="text-xs text-yellow-500 mt-2">
                    * Devi accettare tutte le condizioni per procedere con il pagamento
                  </p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setStep('select')}
                  className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                >
                  Indietro
                </button>
                
                {redirecting ? (
                  <div className="flex-1 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-medium flex items-center justify-center gap-2">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Reindirizzamento a Stripe...</span>
                  </div>
                ) : paymentMethod === 'stripe' ? (
                  <button
                    onClick={handlePayment}
                    disabled={loading || !targetUrl || !customerEmail || !allPoliciesAccepted}
                    className="flex-1 py-3 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <>Paga {formatPrice(getFinalPrice())} con Carta</>
                    )}
                  </button>
                ) : (
                  <div className="flex-1">
                    <PayPalScriptProvider options={{ 
                      clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID || "sb",
                      currency: "EUR"
                    }}>
                      <PayPalButtons
                        style={{ layout: "horizontal", height: 48 }}
                        disabled={!targetUrl || !customerEmail || !allPoliciesAccepted}
                        createOrder={(data, actions) => {
                          return actions.order.create({
                            purchase_units: [{
                              amount: {
                                value: getFinalPrice().toFixed(2),
                                currency_code: "EUR"
                              },
                              description: SERVICES[selectedService].name
                            }]
                          });
                        }}
                        onApprove={handlePayPalApprove}
                        onError={(err) => setError('Errore PayPal: ' + err.message)}
                      />
                    </PayPalScriptProvider>
                  </div>
                )}
              </div>
            </>
          )}

          {step === 'success' && (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="h-10 w-10 text-green-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Grazie per il tuo ordine!</h3>
              <p className="text-gray-400 mb-6">
                Riceverai una email di conferma all'indirizzo <strong className="text-white">{customerEmail}</strong>
              </p>
              <div className="bg-[#0f0f1a] rounded-xl p-4 mb-6 text-left max-w-md mx-auto border border-gray-700">
                <h4 className="font-semibold text-white mb-3">📋 Riepilogo Ordine</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Servizio:</span>
                    <span className="text-white">{SERVICES[selectedService]?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">URL:</span>
                    <span className="text-white">{targetUrl}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Importo:</span>
                    <span className="text-white">{formatPrice(getFinalPrice())} (IVA inclusa)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Consegna:</span>
                    <span className="text-white">Entro 24 ore</span>
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="px-8 py-3 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-lg font-medium hover:opacity-90 transition-all"
              >
                Chiudi
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PremiumServicesModal;
